import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { getPSTDate, getPSTWeekStart, getPSTMonthStart, formatPSTDate, formatPSTDateTime } from '../utils/dateUtils';
import { CashDenominationDisplay, CashDenominationEditor } from '../components/CashDenominationDisplay';

function CashDropValidation() {
  const [data, setData] = useState([]);
  const [dateFrom, setDateFrom] = useState(getPSTDate());
  const [dateTo, setDateTo] = useState(getPSTDate());
  const [showOnlyUnreconciled, setShowOnlyUnreconciled] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [adminCounts, setAdminCounts] = useState({});
  const [reconciliationNotes, setReconciliationNotes] = useState({});
  const [customDenominations, setCustomDenominations] = useState({}); // { [itemId]: { hundreds, fifties, ... } } after row expand (required when there is a delta)
  const [countedCheckedByItem, setCountedCheckedByItem] = useState({}); // per-denom checkbox after physical count
  const [loading, setLoading] = useState(false);
  const [statusMessages, setStatusMessages] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [unreconcileModal, setUnreconcileModal] = useState({ show: false, item: null });

  // Set page title
  useEffect(() => {
    document.title = 'Validation Terminal';
  }, []);
  
  const DENOMINATION_CONFIG = [
    { name: 'hundreds', value: 100, display: 'Hundreds ($100)' },
    { name: 'fifties', value: 50, display: 'Fifties ($50)' },
    { name: 'twenties', value: 20, display: 'Twenties ($20)' },
    { name: 'tens', value: 10, display: 'Tens ($10)' },
    { name: 'fives', value: 5, display: 'Fives ($5)' },
    { name: 'twos', value: 2, display: 'Twos ($2)' },
    { name: 'ones', value: 1, display: 'Ones ($1)' },
    { name: 'half_dollars', value: 0.5, display: 'Half Dollars ($0.50)' },
    { name: 'quarters', value: 0.25, display: 'Quarters ($0.25)' },
    { name: 'dimes', value: 0.10, display: 'Dimes ($0.10)' },
    { name: 'nickels', value: 0.05, display: 'Nickels ($0.05)' },
    { name: 'pennies', value: 0.01, display: 'Pennies ($0.01)' },
  ];
  const ROLLS_DISPLAY = [
    { name: 'quarter_rolls', value: 10, display: 'Quarter Rolls ($10)' },
    { name: 'dime_rolls', value: 5, display: 'Dime Rolls ($5)' },
    { name: 'nickel_rolls', value: 2, display: 'Nickel Rolls ($2)' },
    { name: 'penny_rolls', value: 0.50, display: 'Penny Rolls ($0.50)' },
  ];

  const ALL_DENOM_CHECKBOX_NAMES = [...DENOMINATION_CONFIG.map((d) => d.name), ...ROLLS_DISPLAY.map((d) => d.name)];

  /** Reconcile is allowed only when every ✓ in the expanded denomination editor is ticked for this row. */
  const allDenomCheckboxesTicked = (itemId) => {
    const checks = countedCheckedByItem[itemId];
    if (!checks || typeof checks !== 'object') return false;
    return ALL_DENOM_CHECKBOX_NAMES.every((name) => checks[name] === true);
  };
  
  const toggleRow = (itemId) => {
    setExpandedRows(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleExpandRow = (item) => {
    const currentlyExpanded = expandedRows[item.id];
    if (!currentlyExpanded) {
      // Start with null = unset adjusted count; ✓ empty → cash drop count; ✓ with value → keep value
      setCustomDenominations(prev => ({
        ...prev,
        [item.id]: {
          ...DENOMINATION_CONFIG.reduce((acc, d) => { acc[d.name] = null; return acc; }, {}),
          ...ROLLS_DISPLAY.reduce((acc, d) => { acc[d.name] = null; return acc; }, {})
        }
      }));
      setCountedCheckedByItem(prev => ({
        ...prev,
        [item.id]: Object.fromEntries(ALL_DENOM_CHECKBOX_NAMES.map((n) => [n, false]))
      }));
    }
    toggleRow(item.id);
  };

  const COLORS = {
    magenta: '#AA056C',
    yellowGreen: '#22C55E',
    lightPink: '#F46690',
    gray: '#64748B'
  };

  const showStatusMessage = (itemId, text, type = 'info') => {
    setStatusMessages(prev => ({ ...prev, [itemId]: { text, type, show: true } }));
    setTimeout(() => {
      setStatusMessages(prev => {
        const updated = { ...prev };
        if (updated[itemId]) {
          updated[itemId].show = false;
        }
        return updated;
      });
    }, 5000);
  };

  const fetchData = async () => {
    setLoading(true);
    const token = sessionStorage.getItem('access_token');
    try {
      const response = await fetch(
        `${API_ENDPOINTS.CASH_DROP_RECONCILER}?datefrom=${dateFrom}&dateto=${dateTo}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const result = await response.json();
      setData(Array.isArray(result) ? result : []);
      // Initialize adminCounts for existing items if they have an admin_count_amount
      const initialAdminCounts = {};
      result.forEach(item => {
        if (item.admin_count_amount != null) {
          initialAdminCounts[item.id] = item.admin_count_amount;
        }
      });
      setAdminCounts(initialAdminCounts);
    } catch (err) {
      console.error("Fetch error:", err);
      if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
        showStatusMessage('global', 'Cannot connect to server. Please make sure the backend is running on http://localhost:8000', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [showOnlyUnreconciled]);

  const getCustomDenomSum = (itemId) => {
    const denoms = customDenominations[itemId];
    if (!denoms) return 0;
    const denomSum = DENOMINATION_CONFIG.reduce((sum, d) => {
      const v = denoms[d.name];
      const c = v == null ? 0 : (parseFloat(v) || 0);
      return sum + c * d.value;
    }, 0);
    const rollsSum = ROLLS_DISPLAY.reduce((sum, d) => {
      const v = denoms[d.name];
      const c = v == null ? 0 : (parseFloat(v) || 0);
      return sum + c * d.value;
    }, 0);
    return Math.round((denomSum + rollsSum) * 100) / 100;
  };

  const handleReconcile = async (item) => {
    if (!allDenomCheckboxesTicked(item.id)) {
      showStatusMessage(item.id, 'Tick every denomination checkbox (✓) in the expanded breakdown before reconciling.', 'error');
      return;
    }

    const count = adminCounts[item.id];
    
    if (!count || count === '') {
      showStatusMessage(item.id, 'Please enter a counted amount', 'error');
      return;
    }

    const countedAmount = parseFloat(count);
    const cashDropAmount = parseFloat(item.system_drop_amount);
    const reconcileDelta = countedAmount - cashDropAmount;
    const hasDelta = Math.abs(reconcileDelta) > 0.01;

    if (hasDelta && !customDenominations[item.id]) {
      showStatusMessage(item.id, 'Expand the row (+) to enter the denomination breakdown, then reconcile.', 'error');
      return;
    }

    if (hasDelta && (!reconciliationNotes[item.id] || reconciliationNotes[item.id].trim() === '')) {
      showStatusMessage(item.id, 'Please add reconciliation notes explaining the difference before reconciling.', 'error');
      return;
    }

    if (hasDelta) {
      const customSum = getCustomDenomSum(item.id);
      if (Math.abs(customSum - countedAmount) > 0.02) {
        showStatusMessage(item.id, `Custom denominations must total the counted amount ($${countedAmount.toFixed(2)}). Current total: $${customSum.toFixed(2)}`, 'error');
        return;
      }
    }

    const requestBody = { 
      id: item.id, 
      admin_count_amount: countedAmount,
      reconcile_delta: reconcileDelta,
      is_reconciled: true
    };

    if (hasDelta && reconciliationNotes[item.id]?.trim()) {
      requestBody.notes = reconciliationNotes[item.id].trim();
    }

    if (customDenominations[item.id]) {
      DENOMINATION_CONFIG.forEach(d => {
        const v = customDenominations[item.id][d.name];
        requestBody[d.name] = v == null ? 0 : (typeof v === 'number' ? v : (parseFloat(v) || 0));
      });
      ROLLS_DISPLAY.forEach(d => {
        const v = customDenominations[item.id][d.name];
        requestBody[d.name] = v == null ? 0 : (typeof v === 'number' ? v : (parseFloat(v) || 0));
      });
    }

    const response = await fetch(API_ENDPOINTS.CASH_DROP_RECONCILER, {
      method: 'PATCH',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
      },
      body: JSON.stringify(requestBody)
    });

    if (response.ok) {
      showStatusMessage(item.id, 'Record reconciled successfully', 'success');
      setReconciliationNotes(prev => {
        const updated = { ...prev };
        delete updated[item.id];
        return updated;
      });
      setCustomDenominations(prev => {
        const updated = { ...prev };
        delete updated[item.id];
        return updated;
      });
      setCountedCheckedByItem(prev => {
        const u = { ...prev };
        delete u[item.id];
        return u;
      });
      fetchData();
    } else {
      const err = await response.json();
      showStatusMessage(item.id, err.error || 'Failed to reconcile', 'error');
    }
  };

  const handleUnreconcileClick = (item) => {
    if (item.bank_dropped) {
      showStatusMessage(item.id, 'Cannot unreconcile: this cash drop has been bank dropped.', 'error');
      return;
    }
    setUnreconcileModal({ show: true, item });
  };

  const handleUnreconcileConfirm = async () => {
    if (!unreconcileModal.item) return;

    const item = unreconcileModal.item;
    if (item.bank_dropped) {
      showStatusMessage(item.id, 'Cannot unreconcile: this cash drop has been bank dropped.', 'error');
      setUnreconcileModal({ show: false, item: null });
      return;
    }

    setUnreconcileModal({ show: false, item: null });

    const response = await fetch(API_ENDPOINTS.CASH_DROP_RECONCILER, {
      method: 'PATCH',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
      },
      body: JSON.stringify({ 
        id: item.id, 
        is_reconciled: false
      })
    });

    if (response.ok) {
      showStatusMessage(item.id, 'Record unreconciled successfully', 'success');
      // Clear the admin count and notes for this item so user can enter a new value
      setAdminCounts(prev => {
        const updated = { ...prev };
        delete updated[item.id];
        return updated;
      });
      setReconciliationNotes(prev => {
        const updated = { ...prev };
        delete updated[item.id];
        return updated;
      });
      fetchData();
    } else {
      const err = await response.json();
      showStatusMessage(item.id, err.error || 'Failed to unreconcile', 'error');
    }
  };

  const handleQuickFilter = (type) => {
    let start;
    if (type === 'WTD') {
      start = getPSTWeekStart();
      setDateFrom(start);
      setDateTo(getPSTDate());
      fetchData(start, getPSTDate());
    } else if (type === 'MTD') {
      start = getPSTMonthStart();
      setDateFrom(start);
      setDateTo(getPSTDate());
      fetchData(start, getPSTDate());
    } else {
      start = getPSTDate();
      setDateFrom(start);
      setDateTo(getPSTDate());
      fetchData(start, getPSTDate());
    }
  };

  const filteredList = showOnlyUnreconciled ? data.filter(d => !d.is_reconciled) : data;

  return (
    <div className="min-h-screen bg-gray-100 p-2 md:p-4" style={{ fontFamily: 'Calibri, Verdana, sans-serif', fontSize: '14px', color: COLORS.gray }}>
      <div className="max-w-[1400px] mx-auto bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
        
        {/* HEADER & FILTERS */}
        <div className="p-4 md:p-6 border-b bg-white">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 md:gap-6">
            <div>
              <h2 className="font-black uppercase italic tracking-tighter" style={{ fontSize: '24px' }}>Validation <span style={{ color: COLORS.magenta }}>Terminal</span></h2>
              <p className="text-xs font-bold tracking-widest uppercase" style={{ color: COLORS.gray, fontSize: '14px' }}>Audit & Reconciliation</p>
            </div>

            <div className="flex flex-wrap items-end gap-2 md:gap-4 bg-gray-50 p-3 md:p-4 rounded-lg border w-full lg:w-auto">
              {/* Custom Date Inputs */}
              <div className="flex flex-col">
                <label className="text-xs font-black uppercase mb-1" style={{ color: COLORS.gray, fontSize: '14px' }}>From</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} autoComplete="off" className="p-2 border rounded bg-white" style={{ fontSize: '14px' }} />
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-black uppercase mb-1" style={{ color: COLORS.gray, fontSize: '14px' }}>To</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} autoComplete="off" className="p-2 border rounded bg-white" style={{ fontSize: '14px' }} />
              </div>
              
              <button onClick={fetchData} className="text-white px-3 md:px-4 py-2 rounded font-bold transition" style={{ backgroundColor: COLORS.magenta, fontSize: '14px' }}>Fetch</button>

              <div className="flex gap-1 border-l pl-2 md:pl-4">
                <button onClick={() => handleQuickFilter('WTD')} className="px-2 md:px-3 py-2 bg-white border rounded font-black hover:bg-gray-50" style={{ fontSize: '14px' }}>WTD</button>
                <button onClick={() => handleQuickFilter('MTD')} className="px-2 md:px-3 py-2 bg-white border rounded font-black hover:bg-gray-50" style={{ fontSize: '14px' }}>MTD</button>
              </div>

              {/* Status Toggle */}
              <div className="flex items-center gap-2 md:gap-3 border-l pl-2 md:pl-4">
                 <span className={`text-xs font-black uppercase ${!showOnlyUnreconciled ? 'text-pink-600' : 'text-gray-300'}`} style={{ fontSize: '14px' }}>All</span>
                 <button 
                  onClick={() => setShowOnlyUnreconciled(!showOnlyUnreconciled)}
                  className={`w-12 h-6 rounded-full relative transition-colors ${showOnlyUnreconciled ? 'bg-pink-600' : 'bg-gray-300'}`}
                  style={{ backgroundColor: showOnlyUnreconciled ? COLORS.magenta : undefined }}
                 >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${showOnlyUnreconciled ? 'left-7' : 'left-1'}`} />
                 </button>
                 <span className={`text-xs font-black uppercase ${showOnlyUnreconciled ? 'text-pink-600' : 'text-gray-300'}`} style={{ fontSize: '14px', color: showOnlyUnreconciled ? COLORS.magenta : undefined }}>Pending</span>
              </div>
            </div>
          </div>
        </div>

        {/* Global Status Message */}
        {statusMessages.global && statusMessages.global.show && (
          <div className={`mx-4 md:mx-6 mt-4 p-3 rounded-lg border-l-4 ${
            statusMessages.global.type === 'error' ? 'bg-red-50 border-red-500' : 
            statusMessages.global.type === 'success' ? 'bg-green-50 border-green-500' : 
            'bg-blue-50 border-blue-500'
          }`}>
            <p className={`font-bold ${statusMessages.global.type === 'error' ? 'text-red-700' : statusMessages.global.type === 'success' ? 'text-green-700' : 'text-blue-700'}`} style={{ fontSize: '14px' }}>
              {statusMessages.global.text}
            </p>
          </div>
        )}

        {/* DATA TABLE */}
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 font-black uppercase border-b" style={{ color: COLORS.gray }}>
                <th className="p-2 md:p-4" style={{ fontSize: '14px' }}>Date / Shift / Register / User</th>
                <th className="p-2 md:p-4" style={{ fontSize: '14px' }}>Cash Drop</th>
                <th className="p-2 md:p-4" style={{ fontSize: '14px' }}>Cash Drop Receipt Amount</th>
                <th className="p-2 md:p-4" style={{ fontSize: '14px' }}>CashDrop Variance</th>
                <th className="p-2 md:p-4" style={{ fontSize: '14px' }}>Reconcile Delta</th>
                <th className="p-2 md:p-4" style={{ fontSize: '14px' }}>Counted Amount</th>
                <th className="p-2 md:p-4" style={{ fontSize: '14px' }}>Action</th>
                <th className="p-2 md:p-4" style={{ fontSize: '14px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredList.length > 0 ? filteredList.map(item => {
                const countedAmount = parseFloat(adminCounts[item.id] || 0);
                const cashDropAmount = parseFloat(item.system_drop_amount || 0);
                const reconcileDelta = countedAmount - cashDropAmount;
                const hasDelta = Math.abs(reconcileDelta) > 0.01;
                
                // Safely parse reconcile_delta from item
                const itemReconcileDelta = item.reconcile_delta != null ? parseFloat(item.reconcile_delta) : 0;
                const displayReconcileDelta = isNaN(itemReconcileDelta) ? 0 : itemReconcileDelta;
                const isExpanded = expandedRows[item.id];
                const rawCounted = adminCounts[item.id];
                const parsedCounted = parseFloat(rawCounted);
                const countedIsEmptyOrZero =
                  rawCounted === '' ||
                  rawCounted === undefined ||
                  rawCounted === null ||
                  (Number.isFinite(parsedCounted) && parsedCounted === 0);
                const showValidateCountBtn = !item.is_reconciled && !isExpanded && countedIsEmptyOrZero;
                const reconciliationNotesTrim = (reconciliationNotes[item.id] || '').trim();
                const reconcileNotesRequired = hasDelta && !reconciliationNotesTrim;
                const reconcileButtonDisabled = !allDenomCheckboxesTicked(item.id) || reconcileNotesRequired;
                const reconcileButtonTitle = !allDenomCheckboxesTicked(item.id)
                  ? 'Tick every denomination ✓ in the expanded breakdown before reconciling.'
                  : reconcileNotesRequired
                    ? 'Reconciliation notes are required: explain the difference between counted amount and cash drop.'
                    : undefined;
                
                return (
                  <React.Fragment key={item.id}>
                    <tr className="border-b hover:bg-pink-50/30 transition-colors">
                      <td className="p-2 md:p-4">
                        <div className="font-black" style={{ fontSize: '14px' }}>{formatPSTDate(item.date)}</div> 
                        <div className="font-bold uppercase" style={{ color: COLORS.magenta, fontSize: '14px' }}>{item.shift_number} | {item.workstation} | {item.user_name}</div>
                        {item.submitted_at && (
                          <div className="italic mt-1" style={{ color: COLORS.gray, fontSize: '13px' }}>
                            Submitted: {formatPSTDateTime(null, item.submitted_at)}
                          </div>
                        )}
                      </td>
                      <td className="p-2 md:p-4">
                        {item.label_image_url ? (
                          <button 
                            onClick={() => { setSelectedImage(item.label_image_url); setImageLoadError(false); }}
                            className="font-black border-b-2 border-dotted hover:text-pink-600 transition-colors"
                            style={{ fontSize: '14px', color: COLORS.gray, borderColor: COLORS.lightPink }}
                          >
                            ${item.system_drop_amount}
                          </button>
                        ) : (
                          <span className="font-black" style={{ fontSize: '14px', color: COLORS.gray }}>${item.system_drop_amount}</span>
                        )}
                        {item.label_image_url && (
                          <div className="uppercase mt-1" style={{ color: COLORS.gray, fontSize: '14px' }}>Click to view receipt</div>
                        )}
                      </td>
                      <td className="p-2 md:p-4 font-bold" style={{ fontSize: '14px', color: COLORS.gray }}>${item.ws_label_amount}</td>
                      <td
                        className={`p-2 md:p-4 font-black ${(parseFloat(item.variance || 0) !== 0) ? 'text-red-500' : 'text-gray-300'}`}
                        style={{ fontSize: '14px' }}
                        title="Cash drop total − receipt amount"
                      >
                        {`$${(isNaN(parseFloat(item.variance)) ? 0 : parseFloat(item.variance)).toFixed(2)}`}
                      </td>
                      <td className={`p-2 md:p-4 font-black ${(item.is_reconciled ? displayReconcileDelta !== 0 : reconcileDelta !== 0) ? 'text-red-500' : 'text-gray-300'}`} style={{ fontSize: '14px' }} title="Adjusted total − Cash drop total">
                        {item.is_reconciled ? `$${displayReconcileDelta.toFixed(2)}` : `$${isNaN(reconcileDelta) ? '0.00' : reconcileDelta.toFixed(2)}`}
                      </td>
                      <td className="p-2 md:p-4">
                        {item.is_reconciled ? (
                          <span className="font-black" style={{ fontSize: '14px', color: COLORS.yellowGreen }}>${item.admin_count_amount}</span>
                        ) : showValidateCountBtn ? (
                          <button
                            type="button"
                            onClick={() => handleExpandRow(item)}
                            className="text-white font-black px-3 py-2 rounded-lg shadow-md transition-all active:scale-95 whitespace-nowrap"
                            style={{ backgroundColor: COLORS.magenta, fontSize: '12px' }}
                          >
                            VALIDATE COUNT
                          </button>
                        ) : (
                          <div className="flex items-center bg-white border rounded px-2 py-1 w-24 md:w-32 focus-within:ring-2 ring-pink-500">
                            <span className="mr-1" style={{ color: COLORS.gray }}>$</span>
                            <input
                              type="text"
                              className="w-full font-bold outline-none"
                              style={{ fontSize: '14px' }}
                              autoComplete="off"
                              readOnly={!!(isExpanded && customDenominations[item.id])}
                              onChange={(e) => !(isExpanded && customDenominations[item.id]) && setAdminCounts({ ...adminCounts, [item.id]: e.target.value })}
                              value={adminCounts[item.id] || ''}
                              title={isExpanded && customDenominations[item.id] ? 'Driven by denomination breakdown below' : ''}
                            />
                          </div>
                        )}
                      </td>
                      <td className="p-2 md:p-4">
                        {item.is_reconciled ? (
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-1" style={{ color: COLORS.yellowGreen }}>
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                              <span className="font-black uppercase" style={{ fontSize: '14px' }}>Reconciled</span>
                            </div>
                            {item.reconciliation_notes && (
                              <div className="p-2 bg-gray-50 rounded border border-gray-200">
                                <p className="text-xs font-bold uppercase mb-1" style={{ color: COLORS.gray, fontSize: '14px' }}>Notes:</p>
                                <p className="text-xs italic" style={{ color: COLORS.gray, fontSize: '14px' }}>{item.reconciliation_notes}</p>
                              </div>
                            )}
                            {item.bank_dropped && (
                              <p className="text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" style={{ fontSize: '12px' }}>
                                Bank dropped — unreconcile is not allowed.
                              </p>
                            )}
                            <button 
                              type="button"
                              onClick={() => handleUnreconcileClick(item)}
                              disabled={!!item.bank_dropped}
                              title={item.bank_dropped ? 'Cannot unreconcile after bank drop' : undefined}
                              className="text-white font-black px-2 md:px-3 py-1.5 rounded-lg shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ backgroundColor: COLORS.gray, fontSize: '14px' }}
                            >
                              UNRECONCILE
                            </button>
                            {statusMessages[item.id] && statusMessages[item.id].show && (
                              <div className={`p-2 rounded ${
                                statusMessages[item.id].type === 'error' ? 'bg-red-50 text-red-700' : 
                                statusMessages[item.id].type === 'success' ? 'bg-green-50 text-green-700' : 
                                'bg-blue-50 text-blue-700'
                              }`} style={{ fontSize: '14px' }}>
                                {statusMessages[item.id].text}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2">
                            {/* Overlay: disabled buttons often do not receive pointer events; instant CSS tooltip (no native title delay). */}
                            <span className="group relative inline-flex self-start rounded-lg">
                              <button
                                type="button"
                                onClick={() => handleReconcile(item)}
                                disabled={reconcileButtonDisabled}
                                className="text-white font-black px-3 md:px-4 py-2 rounded-lg shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: COLORS.magenta, fontSize: '14px' }}
                              >
                                RECONCILE
                              </button>
                              {reconcileButtonDisabled && reconcileButtonTitle ? (
                                <>
                                  <span className="absolute inset-0 z-[1] cursor-help rounded-lg" aria-hidden />
                                  <span
                                    role="tooltip"
                                    className="pointer-events-none absolute left-1/2 bottom-full z-[100] mb-2 w-max max-w-[min(20rem,calc(100vw-2rem))] -translate-x-1/2 rounded-md px-2.5 py-1.5 text-left text-xs font-semibold leading-snug opacity-0 shadow-lg group-hover:opacity-100 whitespace-normal border border-white/20"
                                    style={{ transition: 'none', backgroundColor: COLORS.magenta, color: '#ffffff' }}
                                  >
                                    {reconcileButtonTitle}
                                  </span>
                                </>
                              ) : null}
                            </span>
                            {statusMessages[item.id] && statusMessages[item.id].show && (
                              <div className={`p-2 rounded ${
                                statusMessages[item.id].type === 'error' ? 'bg-red-50 text-red-700' : 
                                statusMessages[item.id].type === 'success' ? 'bg-green-50 text-green-700' : 
                                'bg-blue-50 text-blue-700'
                              }`} style={{ fontSize: '14px' }}>
                                {statusMessages[item.id].text}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-2 md:p-4">
                        <button
                          onClick={() => handleExpandRow(item)}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                          style={{ color: COLORS.gray }}
                        >
                          {isExpanded ? '−' : '+'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan="8" className="p-6 md:p-8 align-top">
                          <div className="w-full max-w-7xl mx-auto min-w-0 flex flex-col gap-6 md:gap-8">
                            {/* TOP: Receipt & notes — same max width as cash editor below */}
                            <div className="bg-white border rounded-xl p-5 shadow-sm w-full">
                              <h4 className="font-black uppercase mb-4 tracking-widest border-b pb-2" style={{ fontSize: '14px', color: COLORS.gray }}>Notes</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="min-w-0">
                                  <label className="block text-xs font-bold uppercase mb-1" style={{ color: COLORS.gray, fontSize: '14px' }}>
                                    Reconciliation notes{' '}
                                    {!item.is_reconciled && (
                                      hasDelta
                                        ? '(counted amount differs from cash drop)'
                                        : '(optional)'
                                    )}
                                  </label>
                                  {!item.is_reconciled ? (
                                    <textarea
                                      value={reconciliationNotes[item.id] || ''}
                                      onChange={(e) => setReconciliationNotes({ ...reconciliationNotes, [item.id]: e.target.value })}
                                      rows={3}
                                      autoComplete="off"
                                      className="w-full p-2 border rounded-lg resize-none focus:ring-2 focus:ring-pink-500 outline-none"
                                      placeholder={hasDelta ? 'Explain the difference between counted amount and cash drop…' : 'Optional — add a note if needed…'}
                                      style={{ fontSize: '14px' }}
                                    />
                                  ) : item.reconciliation_notes ? (
                                    <p className="mt-1 italic text-sm break-words p-2 bg-gray-50 rounded border" style={{ color: COLORS.gray }}>{item.reconciliation_notes}</p>
                                  ) : (
                                    <p className="mt-1 text-xs italic" style={{ color: COLORS.gray }}>No reconciliation notes</p>
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <span className="text-xs font-bold uppercase" style={{ color: COLORS.gray }}>Employee notes</span>
                                  {item.notes ? (
                                    <p className="mt-1 italic text-sm break-words p-2 bg-gray-50 rounded border min-h-[80px]" style={{ color: COLORS.gray }}>{item.notes}</p>
                                  ) : (
                                    <p className="mt-1 text-xs italic min-h-[80px]" style={{ color: COLORS.gray }}>No notes</p>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* BOTTOM: Cash drop total (same max width as Notes) */}
                            <div className="w-full min-w-0 overflow-visible">
                              {item.is_reconciled ? (
                                <div className="bg-white border rounded-xl p-6 md:p-8 shadow-sm overflow-visible">
                                  <CashDenominationDisplay
                                    record={item}
                                    title="Denominations (counted / final after reconciliation)"
                                    grayColor={COLORS.gray}
                                    magentaColor={COLORS.yellowGreen}
                                    spacious
                                  />
                                </div>
                              ) : customDenominations[item.id] ? (
                                <div className="bg-white border rounded-xl p-6 md:p-8 shadow-sm overflow-visible">
                                  <CashDenominationEditor
                                    values={customDenominations[item.id]}
                                    systemRecord={item}
                                    countedChecked={countedCheckedByItem[item.id] || {}}
                                    onCountedCheckedChange={(name, checked) => {
                                      setCountedCheckedByItem(prev => ({
                                        ...prev,
                                        [item.id]: { ...(prev[item.id] || {}), [name]: checked }
                                      }));
                                    }}
                                    onChange={(name, val) => {
                                      setCustomDenominations(prev => ({
                                        ...prev,
                                        [item.id]: { ...prev[item.id], [name]: val }
                                      }));
                                    }}
                                    onTotalChange={(total) => {
                                      setAdminCounts(prev => ({ ...prev, [item.id]: total > 0 ? String(total) : '' }));
                                    }}
                                    targetTotal={parseFloat(adminCounts[item.id] || 0) || 0}
                                    grayColor={COLORS.gray}
                                    magentaColor={COLORS.magenta}
                                    greenColor={COLORS.yellowGreen}
                                    sectionTitle="Cash drop total"
                                    spacious
                                    maxWidthClass="max-w-full"
                                  />
                                </div>
                              ) : (
                                <div className="bg-white border rounded-xl p-6 md:p-8 shadow-sm overflow-visible">
                                  <CashDenominationDisplay
                                    record={item}
                                    title="Cash drop total"
                                    grayColor={COLORS.gray}
                                    magentaColor={COLORS.magenta}
                                    spacious
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              }) : (
                <tr>
                  <td colSpan="7" className="p-20 text-center italic font-bold uppercase tracking-widest" style={{ color: COLORS.gray, fontSize: '14px' }}>
                    {loading ? "Loading Records..." : "No records found for this period"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL FOR IMAGE */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm">
          <div className="relative max-w-4xl w-full bg-white rounded-lg p-2 overflow-hidden shadow-2xl">
            <button 
              onClick={() => { setSelectedImage(null); setImageLoadError(false); }}
              className="absolute top-4 right-4 bg-red-600 text-white w-8 h-8 rounded-full font-bold flex items-center justify-center hover:bg-red-700 z-10"
            >
              ✕
            </button>
            <div className="max-h-[80vh] overflow-y-auto min-h-[200px] flex items-center justify-center">
              {imageLoadError ? (
                <div className="p-8 text-center">
                  <p className="font-bold text-gray-600 mb-2" style={{ fontSize: '16px' }}>Image unavailable</p>
                  <p className="text-sm text-gray-500">The receipt image could not be loaded. It may have been moved or the connection failed.</p>
                </div>
              ) : (
                <img 
                  src={selectedImage || ''} 
                  alt="Cash Drop Receipt" 
                  className="w-full h-auto"
                  onLoad={() => setImageLoadError(false)}
                  onError={() => setImageLoadError(true)}
                />
              )}
            </div>
            <div className="p-4 bg-gray-50 text-center">
              <p className="font-black uppercase tracking-tighter" style={{ color: COLORS.gray, fontSize: '14px' }}>Verified Cash Drop Receipt</p>
            </div>
          </div>
        </div>
      )}

      {/* UNRECONCILE CONFIRMATION MODAL */}
      {unreconcileModal.show && unreconcileModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="relative max-w-md w-full bg-white rounded-lg shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-4" style={{ backgroundColor: COLORS.magenta }}>
              <h3 className="text-white font-black uppercase tracking-widest text-center" style={{ fontSize: '18px' }}>
                Confirm Unreconcile
              </h3>
            </div>
            
            {/* Body */}
            <div className="p-6">
              <p className="mb-4 text-center" style={{ fontSize: '14px', color: COLORS.gray }}>
                Are you sure you want to unreconcile this record?
              </p>
              <p className="mb-6 text-center italic" style={{ fontSize: '14px', color: COLORS.gray }}>
                This will allow you to edit the counted amount again.
              </p>
              
              {/* Item Details */}
              {unreconcileModal.item && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold uppercase" style={{ fontSize: '14px', color: COLORS.gray }}>Date:</span>
                    <span className="font-black" style={{ fontSize: '14px', color: COLORS.gray }}>{formatPSTDate(unreconcileModal.item.date)}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold uppercase" style={{ fontSize: '14px', color: COLORS.gray }}>Register:</span>
                    <span className="font-black" style={{ fontSize: '14px', color: COLORS.magenta }}>{unreconcileModal.item.workstation}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-bold uppercase" style={{ fontSize: '14px', color: COLORS.gray }}>Counted Amount:</span>
                    <span className="font-black" style={{ fontSize: '14px', color: COLORS.yellowGreen }}>${unreconcileModal.item.admin_count_amount}</span>
                  </div>
                </div>
              )}
              
              {/* Buttons */}
              <div className="flex flex-col md:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleUnreconcileConfirm}
                  disabled={!!unreconcileModal.item?.bank_dropped}
                  className="flex-1 text-white font-black px-4 py-3 rounded-lg shadow-md transition-all active:scale-95 uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: COLORS.magenta, fontSize: '14px' }}
                >
                  Confirm
                </button>
                <button
                  onClick={() => setUnreconcileModal({ show: false, item: null })}
                  className="flex-1 text-white font-black px-4 py-3 rounded-lg shadow-md transition-all active:scale-95 uppercase tracking-widest"
                  style={{ backgroundColor: COLORS.gray, fontSize: '14px' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CashDropValidation;
